/**
 * Token Utilities
 *
 * Shared JWT token helpers for session management.
 * Used by both Auth0 and OIDC session hooks.
 */

import { jwtDecode } from 'jwt-decode';
import { getAuthFromLocalStorage } from './authUtils';
import { ONE_YEAR_MS } from '../sessionConfig';

// ============================================================================
// TOKEN EXPIRATION
// ============================================================================

/**
 * Get token expiration from localStorage
 * @returns {number|null} Expiration timestamp in ms, or null if no valid token
 */
export const getTokenExpiration = () => {
  const authData = getAuthFromLocalStorage();
  const token = authData?.access_token;

  if (!token) return null;

  try {
    const decoded = jwtDecode(token);
    if (!decoded?.exp) return null;
    const expMs = decoded.exp * 1000;
    // Sanity check: reject obviously invalid timestamps (more than 1 year in future)
    const oneYearFromNow = Date.now() + ONE_YEAR_MS;
    if (expMs > oneYearFromNow) {
      console.warn('[tokenUtils] Token exp claim seems invalid:', new Date(expMs));
      return null;
    }
    return expMs;
  } catch (error) {
    console.error('[tokenUtils] Failed to decode token for expiration:', error);
    return null;
  }
};

/**
 * Get token lifetime in ms (exp - iat)
 * @returns {number|null} Lifetime in ms, or null if unavailable
 */
export const getTokenLifetime = () => {
  const authData = getAuthFromLocalStorage();
  const token = authData?.access_token;

  if (!token) return null;

  try {
    const decoded = jwtDecode(token);
    if (decoded?.exp && decoded?.iat) {
      return (decoded.exp - decoded.iat) * 1000;
    }
  } catch (error) {
    // Ignore decode errors
  }
  return null;
};

/**
 * Check if a token is expired or about to expire
 * @param {string} token - The JWT access token
 * @param {number} bufferSeconds - Buffer time before actual expiry (default 30 seconds)
 * @returns {boolean} True if the token is expired or will expire within the buffer time
 */
export const isTokenExpired = (token, bufferSeconds = 30) => {
  if (!token) return true;

  try {
    const decoded = jwtDecode(token);
    if (!decoded?.exp) return true;

    const expiresAtMs = decoded.exp * 1000;
    const now = Date.now();
    const bufferMs = bufferSeconds * 1000;

    return now >= expiresAtMs - bufferMs;
  } catch (error) {
    console.error('[tokenUtils] Error decoding token:', error);
    return true;
  }
};

/**
 * Get token issued at timestamp (iat)
 * @returns {number|null} IAT timestamp in ms, or null if unavailable
 */
export const getTokenIssuedAt = () => {
  const authData = getAuthFromLocalStorage();
  const token = authData?.access_token;

  if (!token) return null;

  try {
    const decoded = jwtDecode(token);
    if (decoded?.iat) {
      return decoded.iat * 1000;
    }
  } catch (error) {
    // Ignore decode errors
  }
  return null;
};

// ============================================================================
// TOKEN DECODE
// ============================================================================

/**
 * Decode a JWT token and return its claims
 * @param {string} token - The JWT token
 * @returns {Object|null} Decoded token claims or null on error
 */
export const decodeToken = (token) => {
  if (!token) return null;
  try {
    return jwtDecode(token);
  } catch (error) {
    console.error('[tokenUtils] Failed to decode token:', error);
    return null;
  }
};
